import React, { memo, useMemo, useState } from 'react';
import useEffectOnce from '../hooks/useEffectOnce';
import { useDispatch, useSelector } from 'react-redux';
import { fetchData, fetchPieChartData, fetchDonorChartData, fetchTableData } from '../reduxactions/donorrsactions/DonorReduxAction';
import { RootState } from '../reducers';
import { Panel } from 'primereact/panel';
import { Chart } from 'primereact/chart';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

const DonorDashboard: React.FC = () => {
  const uid = window.sessionStorage.getItem('uid');
  const [globalFilter, setGlobalFilter] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffectOnce(() => {
    const fetchDataAsync = async () => {
      try {
        await dispatch(fetchData(uid) as any);
        await dispatch(fetchPieChartData() as any);
        await dispatch(fetchDonorChartData(uid) as any);
        await dispatch(fetchTableData(uid) as any);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchDataAsync();
  }, [dispatch]);

  const data = useSelector((state: RootState) => state.ddashboard.data);
  const piechartData = useSelector((state: RootState) => state.ddashboard.pieChartData);
  const managerChartData = useSelector((state: RootState) => state.ddashboard.donorChartData);
  const piechartOptions = useSelector((state: RootState) => state.ddashboard.piechartOptions);
  const managerChartOptions = useSelector((state: RootState) => state.ddashboard.donorChart);
  const donorTableData = useSelector((state: RootState) => state.ddashboard.donorTableData);

  const summary = data?.[0];
  const isLoading = !summary;
  const url1 = window.sessionStorage.getItem('apiRoot');
  const IUrl = `${url1}/help/donorsdashboard.php`;

  const stats = useMemo(
    () => [
      {
        label: 'Needs',
        value: summary?.needcount,
        icon: 'pi pi-heart-fill',
        className: 'app-stat-card--rose'
      },
      {
        label: 'Donations',
        value: summary?.donations,
        icon: 'pi pi-dollar',
        className: 'app-stat-card--green'
      },
      {
        label: 'Fund Managers',
        value: summary?.org,
        icon: 'pi pi-user',
        className: 'app-stat-card--blue'
      }
    ],
    [summary]
  );

  const openWindow = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, targetUrl: string): void => {
    event.preventDefault();
    const newWindowReference = window.open(
      targetUrl,
      'newwindow',
      'width=1000,height=1000,noopener,noreferrer'
    );

    if (newWindowReference) {
      newWindowReference.opener = null;
    }
  };

  return (
    <div className="admin-layout-container">
      <div className="row">
        <div className="main-content">
          <div className="content-wrapper">
            <section className="app-page-header">
              <div>
                <h1 className="app-page-title">Dashboard</h1>
              </div>
              <a title="help" onClick={(e) => openWindow(e, IUrl)} className="app-help-link">
                Help for this Page
                <i className="pi pi-question-circle"></i>
              </a>
            </section>

            <section className="content">
              <div className="grid">
                {stats.map((stat) => (
                  <div className="col-12 md:col-4" key={stat.label}>
                    <div className={`app-stat-card ${stat.className}`}>
                      <div className="app-stat-card__content">
                        <div>
                          <span className="app-stat-card__label">{stat.label}</span>
                          <div className="app-stat-card__value">
                            {isLoading ? <Skeleton width="5rem" height="2rem" /> : stat.value}
                          </div>
                        </div>
                        <span className="app-stat-card__icon">
                          <i className={stat.icon}></i>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid">
                <section className="col-12 xl:col-6">
                  <Panel
                    header="Monthly Donations"
                    toggleable
                    className="app-panel shadow-3 border-round-2xl"
                  >
                    {isLoading ? (
                      <Skeleton
                        height="450px"
                        borderRadius="20px"
                      />
                    ) : (
                      <div
                        className="flex align-items-center justify-content-center"
                        style={{
                          minHeight: '450px',
                          padding: '1rem'
                        }}
                      >
                        <Chart
                          type="bar"
                          style={{
                            width: '100%',
                            height: '420px'
                          }}
                          data={managerChartData}
                          options={managerChartOptions}
                        />
                      </div>
                    )}
                  </Panel>
                </section>
                <section className="col-12 xl:col-6">
                  <Panel
                    header="Monthly Donations"
                    toggleable
                    className="app-panel shadow-3 border-round-2xl"
                  >
                    {isLoading ? (
                      <Skeleton
                        height="450px"
                        borderRadius="20px"
                      />
                    ) : (
                      <div
                        className="flex align-items-center justify-content-center"
                        style={{
                          minHeight: '450px',
                          padding: '1rem'
                        }}
                      >
                        <Chart type="pie" style={{ height: '450px',width:'100%' }} data={piechartData} options={piechartOptions} />
                      </div>
                    )}
                  </Panel>
                </section>
              </div>
            </section>
            <Panel header="Latest Donations" toggleable className="app-panel">
              <section className="content">
                <div className="app-table-toolbar">
                  <h3 style={{ margin: 0 }}>Latest Donations</h3>
                  <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      placeholder="Global Search"
                    />
                  </span>
                </div>
                {isLoading ? (
                  <Skeleton height="18rem" borderRadius="18px" />
                ) : (
                  <DataTable
                    value={donorTableData}
                    stripedRows
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    tableStyle={{ minWidth: '50rem' }}
                    globalFilter={globalFilter}
                  >
                    <Column body={(_, { rowIndex }) => rowIndex + 1} header="S.No"></Column>
                    <Column field="nameoftheorg" sortable header="Organization Name"></Column>
                    <Column field="name" sortable header="Fundmanager Name"></Column>
                    <Column field="CreateDate" sortable header="Donate Date"></Column>
                    <Column field="Amount" sortable header="Amount"></Column>
                    <Column
                      field="donationStatus"
                      header="Status"
                      sortable
                      body={(rowData) => (
                        <span className="app-status-chip">{rowData.donationStatus}</span>
                      )}
                    />
                  </DataTable>
                )}

                <div className="mt-4">
                  <Button title="View All Donations" onClick={() => navigate('/donations')}>
                    View All Donations
                  </Button>
                </div>
              </section>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(DonorDashboard);
